import type { Substance } from '../types';

export const lsd: Substance = {
  id: 'lsd',
  displayName: 'LSD',
  emoji: '🌈',
  aliases: ['lsd', 'acid', 'psychedelic', 'psychedelics'],
  substanceClass: 'psychedelic',
  mechanismSummary:
    'Partial agonist at serotonin 5-HT2A with unusually slow receptor dissociation, which is a large part ' +
    'of why effects last 8-12 hours from a microgram dose. Increases functional connectivity between brain ' +
    'networks that are normally more segregated.',
  pk: {
    route: 'oral',
    ka: 2.0,
    ke: 0.2665,
    vd: 196,
    bioavailability: 0.71,
    molecularWeightGMol: 323.43,
    brainPlasmaRatio: 1.1,
  },
  doseRange: { min: 0.025, max: 0.3, default: 0.1, step: 0.005, unit: 'µg', displayScale: 1000 },
  bindings: [
    {
      receptor: '5HT2A',
      kiNM: 4,
      action: 'partial-agonist',
      citation:
        'Reported range ~1-15 nM across studies. Rickli A, Hoener MC, Liechti ME receptorome screens; ' +
        'structural context Wacker D et al. (2017) Cell 168:377-389.',
    },
    {
      receptor: '5HT1A',
      kiNM: 1.1,
      action: 'partial-agonist',
      citation: 'LSD is promiscuous across serotonin receptors. NIMH PDSP Ki Database.',
    },
    {
      receptor: 'D2',
      kiNM: 27,
      action: 'partial-agonist',
      citation: 'LSD has genuine dopaminergic activity at higher occupancy. NIMH PDSP Ki Database.',
    },
  ],
  colorPalette: {
    primary: '#ee44ff',
    secondary: '#00ff88',
    particleSpeedMultiplier: 1.8,
    rainbowMode: true,
  },
};

export const psilocybin: Substance = {
  id: 'psilocybin',
  displayName: 'Psilocybin',
  emoji: '🍄',
  aliases: ['psilocybin', 'psilocin', 'mushrooms', 'shrooms'],
  substanceClass: 'psychedelic',
  mechanismSummary:
    'A prodrug rapidly converted to psilocin, which is the active 5-HT2A agonist. Shorter-acting than LSD ' +
    '(4-6 hours) and currently the most studied psychedelic in clinical trials for depression.',
  pk: {
    route: 'oral',
    ka: 1.8,
    ke: 0.462,
    vd: 200,
    bioavailability: 0.5,
    molecularWeightGMol: 284.25,
    brainPlasmaRatio: 1.0,
  },
  doseRange: { min: 1, max: 30, default: 12, step: 1, unit: 'mg', displayScale: 1 },
  bindings: [
    {
      receptor: '5HT2A',
      kiNM: 6,
      action: 'agonist',
      citation:
        'Value is for psilocin, the active metabolite; approximate, reported in the low nanomolar range. ' +
        'NIMH PDSP Ki Database. This model does not simulate the psilocybin to psilocin conversion.',
    },
    {
      receptor: '5HT1A',
      kiNM: 190,
      action: 'agonist',
      citation: 'Approximate, for psilocin. NIMH PDSP Ki Database.',
    },
  ],
  colorPalette: {
    primary: '#c9a0ff',
    secondary: '#66ffcc',
    particleSpeedMultiplier: 1.6,
    rainbowMode: true,
  },
};

export const dmt: Substance = {
  id: 'dmt',
  displayName: 'DMT',
  emoji: '🔮',
  aliases: ['dmt', 'dimethyltryptamine', 'ayahuasca'],
  substanceClass: 'psychedelic',
  mechanismSummary:
    'A very short-acting 5-HT2A agonist — minutes rather than hours, because monoamine oxidase breaks it ' +
    'down almost immediately. Ayahuasca combines it with an MAO inhibitor precisely to prevent that ' +
    'breakdown and make it orally active.',
  pk: {
    route: 'oral',
    ka: 6.0,
    ke: 4.16,
    vd: 130,
    bioavailability: 0.15,
    molecularWeightGMol: 188.27,
    brainPlasmaRatio: 1.3,
  },
  doseRange: { min: 10, max: 60, default: 30, step: 5, unit: 'mg', displayScale: 1 },
  bindings: [
    {
      receptor: '5HT2A',
      kiNM: 127,
      action: 'agonist',
      citation: 'Approximate; DMT has lower 5-HT2A affinity than LSD. NIMH PDSP Ki Database.',
    },
    {
      receptor: '5HT1A',
      kiNM: 75,
      action: 'agonist',
      citation: 'Approximate. NIMH PDSP Ki Database, https://pdsp.unc.edu/databases/kidb.php.',
    },
  ],
  colorPalette: {
    primary: '#ffdd44',
    secondary: '#ff44aa',
    particleSpeedMultiplier: 2.6,
    rainbowMode: true,
  },
};

export const ketamine: Substance = {
  id: 'ketamine',
  displayName: 'Ketamine',
  emoji: '🌀',
  aliases: ['ketamine', 'ket'],
  substanceClass: 'dissociative',
  mechanismSummary:
    'An NMDA receptor antagonist — a different mechanism from the serotonergic psychedelics. Blocking ' +
    'excitatory glutamate signalling produces dissociation and anaesthesia, and at sub-anaesthetic doses ' +
    'it has rapid antidepressant effects that are the subject of substantial clinical research.',
  pk: {
    route: 'oral',
    ka: 2.2,
    ke: 0.2772,
    vd: 210,
    bioavailability: 0.2,
    molecularWeightGMol: 237.73,
    brainPlasmaRatio: 1.6,
  },
  doseRange: { min: 10, max: 150, default: 50, step: 5, unit: 'mg', displayScale: 1 },
  bindings: [
    {
      receptor: 'NMDA',
      kiNM: 500,
      action: 'antagonist',
      citation:
        'Approximate; ketamine is an open-channel NMDA blocker with reported affinity in the high ' +
        'nanomolar to low micromolar range. NIMH PDSP Ki Database.',
    },
    {
      receptor: 'DAT',
      kiNM: 63000,
      action: 'reuptake-inhibitor',
      citation: 'Very weak; included for completeness. NIMH PDSP Ki Database.',
    },
  ],
  colorPalette: { primary: '#66ccff', secondary: '#aa66ff', particleSpeedMultiplier: 0.7 },
};

export const PSYCHEDELICS = [lsd, psilocybin, dmt, ketamine];
